import { useEffect } from 'react';
import { useAnalyticsEvents, type CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CARD_DRAGGABLE_ID_PREFIX } from '../../../services/card-list-dnd';

/**
 * VISIBLE FOR TESTING
 */
// TODO: Handle epic panel cards
// TODO: Handle epic panel drop
// TODO: Handle version drop
export const makeMonitor = ({
	createAnalyticsEvent,
	defaultIssueHierarchyLevel,
}: {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	defaultIssueHierarchyLevel: number;
}): Parameters<typeof monitorForElements>[0] => ({
	onDragStart: ({ source }) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'dragStarted',
			actionSubject: 'card',
		});
		const isSubtask = !!source.data.cardIsSubtask;
		fireUIAnalytics(analyticsEvent, {
			isDivider: source.data.type === 'DIVIDER',
			issueHierarchyLevel: isSubtask ? -1 : defaultIssueHierarchyLevel,
		});
	},
	onDrop: ({ source, location }) => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'dragEnded',
			actionSubject: 'card',
		});
		const isAbort = location.current.dropTargets.length === 0;
		const isRank = location.current.dropTargets[0]?.data.type === CARD_DRAGGABLE_ID_PREFIX;
		const isDivider = source.data.type === 'DIVIDER';
		// When moving from card list to epic/version panel cardListId wont exist in location.
		const isListMove =
			isDivider ||
			(typeof location.current.dropTargets[0]?.data.cardListId !== 'undefined' &&
				location.current.dropTargets[0]?.data.cardListId !== source.data.cardListId);
		const isSubtask = !!source.data.cardIsSubtask;
		fireUIAnalytics(analyticsEvent, {
			isDivider,
			...(isAbort
				? {
						isAbort,
					}
				: {
						isAbort,
						isRank,
						isListMove,
					}),
			issueHierarchyLevel: isSubtask ? -1 : defaultIssueHierarchyLevel,
		});
	},
});

/**
 * Jira Software backlog drag and drop analytics monitoring for pragmatic-dnd
 */
export function useDragAnalytics(defaultIssueHierarchyLevel: number) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(
		() => monitorForElements(makeMonitor({ createAnalyticsEvent, defaultIssueHierarchyLevel })),
		[createAnalyticsEvent, defaultIssueHierarchyLevel],
	);
}
